/**
 * Настройки колонки и блока прямо из шапки таблицы.
 *
 * Раньше заголовки были неприкосновенны: встроенные названия зашиты в
 * интерфейс, а завести или убрать колонку можно было только из меню
 * «Колонки». Шестерёнка в самой ячейке заголовка ставит настройку туда, где
 * на неё смотрят.
 *
 * Переименования хранятся в общих настройках, а не у каждого в браузере:
 * таблица одна на всех, и заголовки в ней тоже общие.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, IconEyeOff, IconTrash, Input, Popover } from '../../../components/index.js'
import { api } from '../../../lib/api.js'
import { useApp } from '../../../store/app-store.js'
import { tableTexts as t } from '../texts.table.js'
import { FIXED_HEAD, groupOrder, groupTitles, type ColumnDef } from './columns.js'
import { RemoveColumnDialog } from './RemoveColumn.js'
import './ColumnSettings.css'

export interface ColumnSettingsProps {
  column: ColumnDef
  /** Весь каталог: из него берётся список блоков, куда колонку можно перенести. */
  catalog: readonly ColumnDef[]
  anchor: HTMLElement | null
  onClose: () => void
  onHide: (code: string) => void
}

export function ColumnSettings({ column, catalog, anchor, onClose, onHide }: ColumnSettingsProps) {
  const bootstrap = useApp((state) => state.bootstrap)
  const toast = useApp((state) => state.toast)
  const reloadBootstrap = useApp((state) => state.reloadBootstrap)

  const [title, setTitle] = useState(column.title)
  const [busy, setBusy] = useState(false)
  const [removing, setRemoving] = useState(false)

  useEffect(() => { setTitle(column.title) }, [column.code, column.title])

  const titles = groupTitles(catalog)
  const groups = groupOrder(catalog).filter((code) => titles[code])
  /* Чекбокс и название бренда держат закреплённую зону — их не переставляем. */
  const movable = !(FIXED_HEAD as readonly string[]).includes(column.code)

  async function moveTo(group: string): Promise<void> {
    if (group === column.group || busy) return
    setBusy(true)
    try {
      await api.renameTableTitle({ kind: 'move', code: column.code, title: group })
      await reloadBootstrap()
      toast('success', t.columnSettings.moved(titles[group] ?? group))
      onClose()
    } catch (err) {
      toast('error', err instanceof Error ? err.message : t.columnSettings.failed)
    } finally {
      setBusy(false)
    }
  }

  const mayManage = bootstrap?.user.permissions.includes('fields.manage') ?? false
  const own = column.field ?? null

  async function rename(): Promise<void> {
    const next = title.trim()
    if (next === '' || next === column.title || busy) return
    setBusy(true)
    try {
      /* У заведённого поля имя живёт в самом поле: его видно и в карточке
         бренда, и в выгрузке, поэтому переименовываем поле, а не колонку. */
      if (own) await api.updateField(own.id, { name: next })
      else await api.renameTableTitle({ kind: 'column', code: column.code, title: next })
      await reloadBootstrap()
      toast('success', t.columnSettings.renamed(next))
      onClose()
    } catch (err) {
      toast('error', err instanceof Error ? err.message : t.columnSettings.failed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Popover open anchor={anchor} onClose={onClose} role="dialog" minWidth={280} maxWidth={320}>
        <div className="col-settings">
          <span className="col-settings__title t-col">{t.columnSettings.title}</span>

          <label className="col-settings__field">
            <span className="t-meta">{t.columnSettings.name}</span>
            <Input
              value={title}
              size="m"
              disabled={busy || (!own && !mayManage)}
              onChange={(event) => setTitle(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void rename() } }}
            />
          </label>
          <Button
            variant="secondary"
            size="s"
            loading={busy}
            disabled={title.trim() === '' || title.trim() === column.title}
            onClick={() => void rename()}
          >
            {t.columnSettings.save}
          </Button>

          {movable ? (
            <label className="col-settings__field">
              <span className="t-meta">{t.columnSettings.group}</span>
              <select
                className="col-settings__select"
                value={column.group}
                disabled={busy}
                onChange={(event) => void moveTo(event.target.value)}
              >
                {groups.map((code) => (
                  <option key={code} value={code}>{titles[code]}</option>
                ))}
              </select>
            </label>
          ) : null}

          {/* Значения справочника правятся там, где они живут: цвет и
              пояснения нужны не только этой колонке. */}
          {column.enumSet ? (
            <Link className="col-settings__link" to={`/catalog/enums?set=${column.enumSet}`}>
              {t.columnSettings.enumLink}
            </Link>
          ) : null}

          <div className="col-settings__actions">
            {column.optional ? (
              <Button variant="ghost" size="s" iconBefore={<IconEyeOff size={16} />} onClick={() => { onHide(column.code); onClose() }}>
                {t.columnsMenu.hide}
              </Button>
            ) : null}
            {own && mayManage ? (
              <Button variant="danger-ghost" size="s" iconBefore={<IconTrash size={16} />} onClick={() => setRemoving(true)}>
                {t.columnSettings.remove}
              </Button>
            ) : null}
          </div>
        </div>
      </Popover>

      {removing && own ? (
        <RemoveColumnDialog
          target={{ kind: 'column', title: column.title, fields: [{ id: own.id, name: column.title }] }}
          onClose={() => setRemoving(false)}
          onDone={onClose}
        />
      ) : null}
    </>
  )
}

export interface GroupSettingsProps {
  group: string
  title: string
  /** Колонки блока: по ним видно, можно ли удалить блок целиком. */
  members?: readonly ColumnDef[]
  anchor: HTMLElement | null
  onClose: () => void
  onAddColumn: () => void
}

export function GroupSettings({ group, title, members = [], anchor, onClose, onAddColumn }: GroupSettingsProps) {
  const bootstrap = useApp((state) => state.bootstrap)
  const toast = useApp((state) => state.toast)
  const reloadBootstrap = useApp((state) => state.reloadBootstrap)

  const [name, setName] = useState(title)
  const [busy, setBusy] = useState(false)
  const [removing, setRemoving] = useState(false)

  useEffect(() => { setName(title); setRemoving(false) }, [group, title])

  const mayManage = bootstrap?.user.permissions.includes('fields.manage') ?? false
  /* Блок удаляется целиком, только если он весь собран из заведённых полей. */
  const own = members.filter((column) => column.field)
  const removable = mayManage && members.length > 0 && own.length === members.length

  async function rename(): Promise<void> {
    const next = name.trim()
    if (next === '' || next === title || busy) return
    setBusy(true)
    try {
      await api.renameTableTitle({ kind: 'group', code: group, title: next })
      await reloadBootstrap()
      toast('success', t.columnSettings.groupRenamed(next))
      onClose()
    } catch (err) {
      toast('error', err instanceof Error ? err.message : t.columnSettings.failed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Popover open anchor={anchor} onClose={onClose} role="dialog" minWidth={280} maxWidth={320}>
      <div className="col-settings">
        <span className="col-settings__title t-col">{t.columnSettings.groupTitle}</span>

        <label className="col-settings__field">
          <span className="t-meta">{t.columnSettings.groupName}</span>
          <Input
            value={name}
            size="m"
            disabled={busy}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void rename() } }}
          />
        </label>
        <Button
          variant="secondary"
          size="s"
          loading={busy}
          disabled={name.trim() === '' || name.trim() === title}
          onClick={() => void rename()}
        >
          {t.columnSettings.save}
        </Button>

        <div className="col-settings__actions">
          <Button variant="ghost" size="s" onClick={() => { onAddColumn(); onClose() }}>
            {t.columnSettings.addColumnHere}
          </Button>
          {removable ? (
            <Button variant="danger-ghost" size="s" iconBefore={<IconTrash size={16} />} onClick={() => setRemoving(true)}>
              {t.removeColumn.groupAction}
            </Button>
          ) : null}
        </div>
      </div>

      {removing ? (
        <RemoveColumnDialog
          target={{
            kind: 'group',
            title,
            fields: own.map((column) => ({ id: column.field!.id, name: column.title })),
          }}
          onClose={() => setRemoving(false)}
          onDone={onClose}
        />
      ) : null}
    </Popover>
  )
}
